import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Lock } from "lucide-react";
import { useApp } from "@/AppContext";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminLogin() {
  const { t, lang, toggleLang, setUser, logout } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      if (data.role !== "admin") {
        await logout();
        setError(t("admin_login_error"));
        return;
      }
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(t("admin_login_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_1px_1px,#f59e0b_1px,transparent_0)] [background-size:28px_28px]" />
      <button
        data-testid="admin-login-lang-btn"
        onClick={toggleLang}
        className="absolute top-6 right-6 font-mono-ps text-xs border border-amber-500/40 rounded-full px-3 py-1 text-amber-400 hover:bg-amber-500/10 transition"
      >
        {lang === "TR" ? "EN" : "TR"}
      </button>

      <div className="relative w-full max-w-md">
        <div className="rounded-2xl border border-amber-500/30 bg-slate-900/80 backdrop-blur-xl p-8 shadow-[0_0_60px_rgba(245,158,11,0.08)]">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mb-4">
              <ShieldAlert className="text-amber-400" size={26} />
            </div>
            <h1 className="font-display text-2xl font-black tracking-wide text-white">{t("admin_login_title")}</h1>
            <p className="text-sm text-slate-400 mt-2">{t("admin_login_desc")}</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs font-mono-ps text-slate-400">{t("email")}</label>
              <Input
                data-testid="admin-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-slate-800/60 border-slate-700 text-white focus-visible:ring-amber-500"
              />
            </div>
            <div>
              <label className="text-xs font-mono-ps text-slate-400">{t("password")}</label>
              <Input
                data-testid="admin-password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-slate-800/60 border-slate-700 text-white focus-visible:ring-amber-500"
              />
            </div>

            {error && <p data-testid="admin-login-error" className="text-red-400 text-sm">{error}</p>}

            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-head font-bold text-base py-6"
            >
              <Lock size={16} className="mr-2" /> {loading ? "..." : t("admin_login_title")}
            </Button>
          </form>

          <button
            data-testid="admin-login-back"
            onClick={() => navigate("/")}
            className="mt-6 w-full text-center text-xs text-slate-500 hover:text-slate-300 transition"
          >
            {t("back_to_site")}
          </button>
        </div>
      </div>
    </div>
  );
}
